import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import './DraftPages.css'

const Icons = {
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  server: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </svg>
  ),
  database: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  cpu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  ),
  wifi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  )
}

// Real services based on AI4Solar infrastructure
const services = [
  {
    name: 'Dashboard Application',
    description: 'Main monitoring dashboard hosted on AWS CloudFront',
    icon: Icons.activity,
    status: 'operational'
  },
  {
    name: 'API Server',
    description: 'REST API at api.ai4solar.org hosted on Render',
    icon: Icons.server,
    status: 'operational'
  },
  {
    name: 'WebSocket Server',
    description: 'Real-time data streaming for live updates',
    icon: Icons.zap,
    status: 'operational'
  },
  {
    name: 'PostgreSQL Database',
    description: 'Primary database with TimescaleDB extension',
    icon: Icons.database,
    status: 'operational'
  },
  {
    name: 'ML Prediction Engine',
    description: 'Predictive maintenance and anomaly detection models',
    icon: Icons.cpu,
    status: 'operational'
  },
  {
    name: 'Inverter Data Pipeline',
    description: 'Fronius Modbus TCP data collection via Pi gateway',
    icon: Icons.wifi,
    status: 'operational'
  },
  {
    name: 'Authentication',
    description: 'User authentication and session management',
    icon: Icons.lock,
    status: 'operational'
  },
  {
    name: 'CDN',
    description: 'CloudFront distribution for static assets',
    icon: Icons.globe,
    status: 'operational'
  }
]

// Real metrics
const metrics = [
  { label: 'Uptime (30 days)', value: '99.9%' },
  { label: 'API Response', value: '<100ms' },
  { label: 'Data Latency', value: '<5s' },
  { label: 'Regions', value: 'EU' }
]

function getStatusLabel(status) {
  switch (status) {
    case 'operational':
      return 'Operational'
    case 'degraded':
      return 'Degraded'
    case 'outage':
      return 'Outage'
    default:
      return 'Unknown'
  }
}

export default function Status() {
  const allOperational = services.every(s => s.status === 'operational')

  return (
    <div className="draft-page status-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">System Status</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              <GradientText gradient="energy">AI4Solar</GradientText> Status
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <div className="status-overall">
              <span className={`status-indicator ${allOperational ? 'operational' : 'degraded'}`} />
              <span className="status-text">
                {allOperational ? 'All Systems Operational' : 'Some Systems Degraded'}
              </span>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <p className="draft-subtitle">
              Current status of all AI4Solar platform services
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Metrics */}
      <section className="draft-section">
        <div className="container">
          <div className="status-metrics">
            {metrics.map((metric, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="status-metric" whileHover={{ scale: 1.02 }}>
                  <div className="status-metric-value">{metric.value}</div>
                  <div className="status-metric-label">{metric.label}</div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="draft-section draft-section-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Service Status</h2>
            <p>Current status of all platform components</p>
          </ScrollReveal>

          <div className="status-services">
            {services.map((service, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <motion.div className="status-service" whileHover={{ x: 5 }}>
                  <span className="status-service-icon">{service.icon}</span>
                  <div className="status-service-info">
                    <h3 className="status-service-name">{service.name}</h3>
                    <p className="status-service-desc">{service.description}</p>
                  </div>
                  <div className="status-service-status">
                    <span className={`status-service-dot ${service.status}`} />
                    <span className={`status-service-label ${service.status}`}>
                      {getStatusLabel(service.status)}
                    </span>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Incidents */}
      <section className="draft-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Recent Incidents</h2>
            <p>Past incidents and scheduled maintenance</p>
          </ScrollReveal>

          <div className="status-incidents">
            <ScrollReveal>
              <div className="status-no-incidents">
                <div className="status-no-incidents-icon">{Icons.check}</div>
                <p>No incidents reported in the last 90 days</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section className="draft-section draft-section-dark">
        <div className="container">
          <ScrollReveal>
            <div className="status-subscribe">
              <h2>Stay Informed</h2>
              <p>Subscribe to receive status updates and incident notifications.</p>
              <div className="status-subscribe-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="status-subscribe-input"
                />
                <motion.button
                  className="status-subscribe-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Subscribe
                </motion.button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
